import type {
  AgentSessionCapabilities,
  AgentSessionEvent,
  AgentSessionViewModel,
} from '@inf-monkeys-tech/monkeys';

import type { AgentWorkbenchEventOf } from './activity-model';

const WORKSPACE_CAPABILITIES = {
  diff: 'diff',
  'workspace-file': 'workspaceFiles',
  terminal: 'terminal',
  'test-result': 'testResults',
  artifact: 'artifacts',
} as const satisfies Partial<
  Record<AgentSessionEvent['eventType'], keyof AgentSessionCapabilities>
>;

export interface AgentWorkbenchTerminalModel {
  terminalId: string;
  stdin: string;
  stdout: string;
  stderr: string;
  status: string;
  exitCode?: number;
  sequence: number;
}

export interface AgentWorkbenchDetailsModel {
  mode: AgentSessionViewModel['snapshot']['mode'];
  modelId: string;
  permissionProfile?: AgentSessionViewModel['snapshot']['permissionProfile'];
  status: AgentSessionViewModel['status'];
  sessionId: string;
  capabilities: AgentSessionCapabilities;
  resumable: boolean;
  summary?: AgentWorkbenchEventOf<'summary'>;
  error?: AgentWorkbenchEventOf<'error'>;
  run?: AgentWorkbenchEventOf<'status'>;
  usage?: AgentWorkbenchEventOf<'usage'>;
  workspace: {
    diffs: AgentWorkbenchEventOf<'diff'>[];
    files: AgentWorkbenchEventOf<'workspace-file'>[];
    terminals: AgentWorkbenchTerminalModel[];
    tests: AgentWorkbenchEventOf<'test-result'>[];
    artifacts: AgentWorkbenchEventOf<'artifact'>[];
  };
}

function latestBy<Type extends AgentSessionEvent['eventType']>(
  events: AgentSessionEvent[],
  eventType: Type,
  key: (event: AgentWorkbenchEventOf<Type>) => string,
): AgentWorkbenchEventOf<Type>[] {
  const latest = new Map<string, AgentWorkbenchEventOf<Type>>();
  for (const event of events) {
    if (event.eventType !== eventType) continue;
    const typedEvent = event as AgentWorkbenchEventOf<Type>;
    latest.set(key(typedEvent), typedEvent);
  }
  return [...latest.values()].sort(
    (left, right) => left.sequence - right.sequence,
  );
}

function projectTerminals(
  events: AgentWorkbenchEventOf<'terminal'>[],
): AgentWorkbenchTerminalModel[] {
  const terminals = new Map<string, AgentWorkbenchTerminalModel>();
  for (const event of events) {
    const current = terminals.get(event.payload.terminalId) ?? {
      terminalId: event.payload.terminalId,
      stdin: '',
      stdout: '',
      stderr: '',
      status: '',
      sequence: event.sequence,
    };
    const stream = event.payload.stream;
    terminals.set(event.payload.terminalId, {
      ...current,
      ...(stream === 'status'
        ? { status: event.payload.chunk }
        : { [stream]: `${current[stream]}${event.payload.chunk}` }),
      ...(event.payload.exitCode === undefined
        ? {}
        : { exitCode: event.payload.exitCode }),
      sequence: event.sequence,
    });
  }
  return [...terminals.values()].sort(
    (left, right) => left.sequence - right.sequence,
  );
}

export function createAgentWorkbenchDetailsModel(
  viewModel: AgentSessionViewModel,
): AgentWorkbenchDetailsModel {
  const capabilities = viewModel.snapshot.capabilities;
  const workspaceEvents = viewModel.events.filter((event) => {
    const capability = WORKSPACE_CAPABILITIES[
      event.eventType as keyof typeof WORKSPACE_CAPABILITIES
    ];
    return capability ? capabilities[capability] === true : false;
  });
  const run = [...viewModel.events]
    .reverse()
    .find(
      (event): event is AgentWorkbenchEventOf<'status'> =>
        event.eventType === 'status',
    );
  const currentRunId = run?.runId;
  const summary = [...viewModel.events]
    .reverse()
    .find(
      (event): event is AgentWorkbenchEventOf<'summary'> =>
        event.eventType === 'summary' &&
        capabilities.summary === true &&
        (!currentRunId || event.runId === currentRunId),
    );

  return {
    mode: viewModel.snapshot.mode,
    modelId: viewModel.snapshot.modelId,
    permissionProfile: viewModel.snapshot.permissionProfile,
    status: viewModel.status,
    sessionId: viewModel.sessionId,
    capabilities,
    resumable: capabilities.resume === true && viewModel.resumable,
    summary,
    error: [...viewModel.events]
      .reverse()
      .find(
        (event): event is AgentWorkbenchEventOf<'error'> =>
          event.eventType === 'error',
      ),
    run,
    usage: capabilities.usage
      ? [...viewModel.events]
          .reverse()
          .find(
            (event): event is AgentWorkbenchEventOf<'usage'> =>
              event.eventType === 'usage',
          )
      : undefined,
    workspace: {
      diffs: latestBy(workspaceEvents, 'diff', (event) => event.payload.path),
      files: latestBy(
        workspaceEvents,
        'workspace-file',
        (event) => event.payload.path,
      ),
      terminals: projectTerminals(
        workspaceEvents.filter(
          (event): event is AgentWorkbenchEventOf<'terminal'> =>
            event.eventType === 'terminal',
        ),
      ),
      tests: latestBy(
        workspaceEvents,
        'test-result',
        (event) => event.payload.testId,
      ),
      artifacts: latestBy(
        workspaceEvents,
        'artifact',
        (event) => event.payload.artifactId,
      ),
    },
  };
}
